import React, {ChangeEvent, FormEvent, Suspense, useEffect, useRef, useState} from "react";
import ReactDOM from "react-dom";
import * as Sentry from "@sentry/browser";
import {IItemContent} from "../../typescript-library";
import window from "../lib/WindowExtended";
import {getShopId} from "../lib/getShopId";
import {Trans, useTranslation} from "react-i18next";
import {useMediaQuery} from "react-responsive";
import {useCustomerSelector} from "../../store";
import {BrowserStorage} from "../../storage/BrowserStorage";
import {isSearchPage} from "../organisms/productListProvider/ProductListProvider";
import {stringConfigParam} from "../lib/ModalWindow/BuildYourOwnString/BuildYourOwnString";
import {Button, ButtonStyles, ButtonType} from "../lib/form/Button/Button";
import genericStyles from "../lib/GenericStyles.module.scss";
import styles from "./HeaderSearch.module.scss";
import {isoLocales} from "../lib/payments/PaymentController";
import ErrorBoundary from "../utils/ErrorBoundary";
import TopSuggestionItem from "./TopSuggestionItem";
import SuggestResultItem from "./SuggestResultItem";
import PrevSearchItem from "./PrevSearchItem";

export type TopSuggestionType = {
  type: string;
  name: string;
  productUrl: string;
  imageUrlFull2x: string;
  imageUrlHalf2x: string;
};

type SuggestResultType = {
  totalSearchHits: number;
  itemContent: IItemContent[];
  topResults: TopSuggestionType[];
};

type ResponseType = {
  result: SuggestResultType | IItemContent[];
};

type SearchStorageItem = {
  term: string;
  url: string;
};

type SearchStorageType = {
  queries: SearchStorageItem[];
};

declare global {
  interface Window {
    c_EvenOdd: "even" | "odd";
  }
}

const KEY_HIT_COUNT = 3;
const MAX_INPUT_DELAY = 600;
const MAX_INPUT_COUNT = 5;
let lastTimestamp = 0;
const inputDelays = [MAX_INPUT_DELAY];

const initSearchStorage: SearchStorageType = {
  queries: [],
};

const browserStorage = new BrowserStorage<SearchStorageType>({
  type: "localStorage",
  dataIdentifier: "SearchSuggestions",
  hoursToCache: 48,
});

let abortController: AbortController | null = null;

/**
 * Search bar in main navigation and search suggestions
 */
const HeaderSearchComponent: React.FC = () => {
  const {t} = useTranslation();
  const {customer} = useCustomerSelector();
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery({query: "(max-width: 767px)"});

  const submitLink = window.c_links.URL_Search || t("url.searchLink");
  const prevSearchItems = browserStorage.read("prev_search")?.queries;
  const header = document.getElementsByClassName("header-full")[0];
  const skipToSearchBtn = document.getElementById("SkipToSearch") as HTMLElement;
  const menuOpenBtnMobile = document.getElementById("MobileNavButton") as HTMLElement;
  const searchOpenBtn = isMobile
    ? (document.getElementById("OpenSearchButtonMobile") as HTMLElement)
    : (document.getElementById("OpenSearchButtonDesktop") as HTMLElement);
  const searchCloseBtn = isMobile
    ? (document.getElementById("CloseSearchButtonMobile") as HTMLElement)
    : (document.getElementById("CloseSearchButtonDesktop") as HTMLElement);

  const [loading, setLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<{term: string; immediate: boolean}>({term: "", immediate: false});
  const [initial, setInitial] = useState<boolean>(false);
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [suggestions, setSuggestions] = useState<IItemContent[]>([]);
  const [topSuggestion, setTopSuggestion] = useState<TopSuggestionType[]>([]);
  const [totalResults, setTotalResults] = useState(0);
  const [storedQueries, setStoredQueries] = useState(prevSearchItems || initSearchStorage.queries);
  const [fadeAnimation, setFadeAnimation] = useState("");
  const [showStringConfigSuggest, setShowStringConfigSuggest] = useState<boolean>(false);
  const debounceTimerRef = useRef<number>();

  // top suggestion item and link for string configurator (build-your-own-string-shelf)
  const stringConfigSuggestUrl = window.location.origin + t("url.stringConfigLink") + "?view=" + stringConfigParam;
  const stringConfigSuggestTerm = "string " + t("general.configurator");

  const stringConfigSuggestItem: TopSuggestionType = {
    type: "configurator",
    name: t("product.customStringShelveConfig.searchTitle"),
    productUrl: stringConfigSuggestUrl,
    imageUrlFull2x: "",
    imageUrlHalf2x: "",
  };

  // See return type. This gives a type assumption
  const isSuggestType = (response: SuggestResultType | IItemContent[]): response is SuggestResultType => {
    return !Array.isArray(response);
  };

  // get search suggestions from api (backend)
  const fetchData = async (path: string, searchParams: string) => {
    if (abortController) {
      abortController.abort();
      abortController = null;
    }

    let queryParams = `currencyCode=${window.c_ActiveCurrency || "EUR"}`;

    // add url params for correct item prices
    if (customer.customerId) {
      queryParams += `&customerId=${customer.customerId}`;
    }
    if (customer.customerGroupId) {
      queryParams += `&customerGroupId=${customer.customerGroupId}`;
    }
    if (window.c_PromotionId) {
      queryParams += `&promotionId=${window.c_PromotionId}`;
    }
    queryParams += "&" + searchParams;
    const getUrl = `${__API_URL__}/shop/${getShopId()}/${path}?${queryParams}`;

    const headers: HeadersInit = {
      "Content-Type": "application/json",
    };
    if (customer.token) {
      headers.Authorization = `Bearer ${customer.token}`;
    }

    try {
      abortController = new AbortController();
      const data = await fetch(getUrl, {
        headers,
        signal: abortController.signal,
        mode: "cors",
      }).then((response) => response.json() as Promise<ResponseType>);
      abortController = null;

      if (isSuggestType(data.result)) {
        setSuggestions(data.result.itemContent);
        setTopSuggestion(data.result.topResults);
        setTotalResults(data.result.totalSearchHits);
      } else {
        setSuggestions(data.result);
        setTotalResults(data.result.length);
      }

      setLoading(false);
    } catch (err) {
      if ((err as Error).name === "AbortError") {
        return;
      }
      (err as Error).name = "HeaderSearch" + (err as Error).name;
      if (__DEVELOPMENT__) {
        // eslint-disable-next-line no-console
        console.log("DEVELOPMENT", err);
      }
      Sentry.captureException(err, {
        extra: {
          info: "Error while loading search suggestions",
          url: `/${path}?${queryParams}`,
        },
      });
    }
  };

  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    const currentTimestamp = Date.now();
    const difference = currentTimestamp - lastTimestamp;
    if (difference < MAX_INPUT_DELAY) {
      inputDelays.push(difference);
      if (inputDelays.length > MAX_INPUT_COUNT) {
        inputDelays.shift();
      }
    }
    lastTimestamp = currentTimestamp;

    if (Math.abs(inputValue.length - searchTerm.term.length) > 1) {
      setSearchTerm({term: inputValue, immediate: true});
    } else {
      setSearchTerm({term: inputValue, immediate: false});
    }

    if (!isMobile && getShopId() !== 100005) checkForStringConfig(inputValue.toLowerCase());

    setInitial(false);
    setLoading(true);
  };

  const clearInput = () => {
    setSearchTerm({term: "", immediate: false});
    setInitial(false);
    inputRef.current?.focus();
  };

  // show string configurator top suggestion if search term includes certain strings
  const checkForStringConfig = (term: string) => {
    if (term.includes("string") || term.includes(t("general.configurator"))) {
      setShowStringConfigSuggest(true);
    } else if (showStringConfigSuggest) {
      setShowStringConfigSuggest(false);
    }
  };

  const newSearchTermOrder = (searchStorageItem: SearchStorageItem) => {
    const alreadyStored = storedQueries.find((item) => item.term === searchStorageItem.term);
    if (alreadyStored) {
      // make an already stored one, the most recent used
      const queryIndex = storedQueries.indexOf(alreadyStored);
      storedQueries.splice(queryIndex, 1);
      storedQueries.push(alreadyStored);
    } else {
      storedQueries.push(searchStorageItem);
    }
    browserStorage.write("prev_search", {
      queries: [...storedQueries],
    });
  };

  // save search term in browser storage
  const storePrevSearchItem = (event: FormEvent) => {
    let itemUrl = "";

    // open string configurator directly if search term includes certain strings
    if (searchTerm.term.toLowerCase().trim() === stringConfigSuggestTerm) {
      event.preventDefault();
      itemUrl = stringConfigSuggestUrl;
    }

    // open the PDP directly if a user enters an item ID
    if (/^[0-9]{6}$/.test(searchTerm.term)) {
      event.preventDefault();
      if (suggestions.length && suggestions[0].itemId.toString() === searchTerm.term) {
        itemUrl = suggestions[0].url;
      } else {
        const search = new URLSearchParams(window.location.search);
        search.delete("q");
        search.set("itm", searchTerm.term);
        itemUrl = window.location.pathname + "?" + search.toString();
      }
    }

    if (searchTerm.term !== "") {
      newSearchTermOrder({term: searchTerm.term.trim(), url: itemUrl});
    } else {
      // Well just stop all submit execution, when the searchTerm is empty
      event.preventDefault();

      return;
    }

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = undefined;
    }
    if (abortController) {
      abortController.abort();
      abortController = null;
    }

    // if not empty, it was a suggestion, or an itemId
    if (itemUrl !== "") {
      window.location.href = itemUrl;
    }
    // if `itemUrl` empty, there should be no `event.preventDefault()`,
    // and thus the form submit action will do its work
  };

  // delete previously saved search term from browser storage
  const removePrevSearchItem = (query: string) => {
    setStoredQueries((oldValues) => {
      return oldValues.filter((text) => text.term !== query);
    });
    inputRef.current?.focus();
  };

  const submitSearch = (query: string) => {
    const queryEncoded = encodeURIComponent(query);
    window.location.href = `${submitLink}?q=${queryEncoded}`;
  };

  const goToSearchSite = (event: React.MouseEvent<HTMLButtonElement>) => {
    storePrevSearchItem(event);
    submitSearch(searchTerm.term);
  };

  const searchPrevSearchItem = (query: string) => {
    const storedSearchTerm = storedQueries.find((item) => item.term === query);

    if (storedSearchTerm) {
      newSearchTermOrder(storedSearchTerm);
    }
    if (abortController) {
      abortController.abort();
      abortController = null;
    }
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = undefined;
    }

    if (
      (/^[0-9]{6}$/.test(query) || query.toLowerCase() === stringConfigSuggestTerm) &&
      storedSearchTerm &&
      storedSearchTerm.url !== ""
    ) {
      window.location.href = storedSearchTerm.url;
    } else {
      submitSearch(query);
    }
  };

  const openSearchField = () => {
    if (!isMobile) {
      setFadeAnimation("fadein");
    }

    if (isMobile && !header.classList.contains("open")) header.classList.add("open");
    if (isMobile && !document.body.classList.contains("no-scroll")) document.body.classList.add("no-scroll");

    if (searchOpenBtn && searchCloseBtn) {
      searchOpenBtn.style.display = "none";
      searchCloseBtn.style.display = "flex";
    }

    setIsVisible(true);
  };

  const closeSearchField = () => {
    if (searchOpenBtn && searchCloseBtn) {
      searchCloseBtn.style.display = "none";
      searchOpenBtn.style.display = "flex";
    }

    if (isMobile) {
      setIsVisible(false);
    } else {
      setFadeAnimation("fadeout");
      setTimeout(() => {
        setIsVisible(false);
      }, 300);
    }
  };

  const closeSearchAndHeader = () => {
    closeSearchField();

    if (isMobile && header.classList.contains("open")) {
      header.classList.remove("open");
      document.body.classList.remove("no-scroll");
    }
  };

  // conditional dependencies to reduce useEffect triggering
  const isSearchPageAndVisible = isSearchPage && isVisible;
  const isDesktopAndVisible = !isMobile && isVisible;

  // show searched term on search result page as long as the user has not yet entered a new search term
  useEffect(() => {
    const lastSearchTerm = storedQueries[storedQueries.length - 1]?.term;
    if (lastSearchTerm && inputRef?.current?.value.trim() === "") {
      setInitial(true);
      setSearchTerm({term: lastSearchTerm, immediate: false});
    }
  }, [isSearchPageAndVisible]);

  // close search field when user clicks outside of search area (desktop only)
  useEffect(() => {
    document.addEventListener("mousedown", (e: MouseEvent) => {
      if (isVisible && containerRef.current && !containerRef.current.contains(e.target as Node)) {
        closeSearchField();
      }
    });
  }, [isDesktopAndVisible]);

  useEffect(() => {
    if (initial || searchTerm.term.length < KEY_HIT_COUNT) {
      return;
    }

    // load search suggestions when input value has at least 3 characters
    const handler = () => {
      if (/^[0-9]{6}$/.test(searchTerm.term)) {
        if (topSuggestion[0]) setTopSuggestion([]);
        void fetchData("items", `view=content&itemIds=${searchTerm.term}`);
      } else {
        let q = encodeURIComponent(searchTerm.term);
        if (getShopId() === 100001 && window.c_EvenOdd === "even") {
          q += "&na=true";
        }
        void fetchData("suggest", `q=${q}`);
      }
    };

    if (searchTerm.immediate) {
      handler();
    } else {
      debounceTimerRef.current = window.setTimeout(handler, Math.min(Math.max(...inputDelays), MAX_INPUT_DELAY));
    }

    return () => {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = undefined;
    };
  }, [searchTerm.term]);

  // open search on click (and on keypress for accessibility reasons)
  useEffect(() => {
    if (searchOpenBtn) searchOpenBtn.addEventListener("click", openSearchField);
    if (skipToSearchBtn) skipToSearchBtn.addEventListener("click", openSearchField);
    if (searchCloseBtn) searchCloseBtn.addEventListener("click", closeSearchAndHeader);
    if (menuOpenBtnMobile) menuOpenBtnMobile.addEventListener("click", closeSearchField);

    const abortSignal = () => {
      abortController?.abort();
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
    window.addEventListener("beforeunload", abortSignal);

    return () => {
      if (searchOpenBtn) searchOpenBtn.removeEventListener("click", openSearchField);
      if (skipToSearchBtn) skipToSearchBtn.removeEventListener("click", openSearchField);
      if (searchCloseBtn) searchCloseBtn.removeEventListener("click", closeSearchAndHeader);
      if (menuOpenBtnMobile) menuOpenBtnMobile.removeEventListener("click", closeSearchField);

      window.removeEventListener("beforeunload", abortSignal);
    };
  }, []);

  useEffect(() => {
    browserStorage.write("prev_search", {
      queries: [...storedQueries],
    });
  }, [storedQueries]);

  useEffect(() => {
    // close search field with "esc" key
    document.onkeyup = function (evt) {
      if (evt.key === "Escape" && isVisible) {
        closeSearchAndHeader();
        if (searchOpenBtn) searchOpenBtn.focus();
      }
    };
  }, [isVisible]);

  if (!isVisible) {
    return null;
  }

  return (
    <div className={`${styles.wrapper} ${fadeAnimation}`}>
      <div ref={containerRef} className={styles.container}>
        {/* region search input field */}
        <form id="Search" className={styles.field} action={submitLink} onSubmit={storePrevSearchItem} method="get">
          <input
            type="search"
            id="SearchTerm"
            name="q"
            spellCheck="false"
            value={searchTerm.term}
            ref={inputRef}
            autoFocus={isVisible}
            onChange={onInputChange}
          />
          {searchTerm.term.length > 0 && (
            <button
              className={styles.fieldButtonRemove}
              type="button"
              aria-label={t("search.deleteSearchTerm")}
              title={t("search.deleteSearchTerm")}
              onClick={clearInput}
            >
              <i className={`icon icon-close ${styles.fieldButtonRemoveIcon}`}>&#160;</i>
            </button>
          )}
          <button
            id="DropdownSearchButton"
            aria-label={t("search.submitSearch")}
            title={t("search.submitSearch")}
            className={styles.fieldButtonSubmit}
            type={"submit"}
          >
            <i className="icon icon-magnifier icon-size--24">&#160;</i>
          </button>
        </form>
        {/* endregion search input field */}

        {/* region previous searches */}
        {storedQueries.length > 0 && (searchTerm.term.length < KEY_HIT_COUNT || initial) && (
          <div className={styles.suggest}>
            <div className={styles.suggestHead}>{t("search.recentlySearched")}</div>
            {storedQueries
              .slice(-5)
              .reverse()
              .map(({term}, index) => (
                <PrevSearchItem
                  key={index}
                  term={term}
                  searchPrevSearchItem={searchPrevSearchItem}
                  removePrevSearchItem={removePrevSearchItem}
                />
              ))}
          </div>
        )}
        {/* endregion previous searches */}

        {/* region search suggestions */}
        {searchTerm.term.length >= KEY_HIT_COUNT && !initial && (
          <div className={styles.suggest}>
            {loading ? (
              <div className={genericStyles.spinnerContainer}>
                <div className="swiper-lazy-preloader">{/* loading spinner container */}</div>
                <br className="clear" />
              </div>
            ) : (
              <>
                {showStringConfigSuggest && (
                  <TopSuggestionItem item={stringConfigSuggestItem} onClick={storePrevSearchItem} />
                )}
                {topSuggestion && topSuggestion[0] && (
                  <TopSuggestionItem item={topSuggestion[0]} onClick={storePrevSearchItem} />
                )}
                <div className={styles.suggestHead}>
                  {totalResults ? (
                    t("product.products--counts", {
                      counts: totalResults.toLocaleString(isoLocales[getShopId()].locale),
                    })
                  ) : (
                    <Trans
                      i18nKey="search.noResultsGiven--query"
                      ns="translations"
                      components={{
                        s: <strong />,
                      }}
                      values={{query: searchTerm.term}}
                    />
                  )}
                </div>
                {suggestions.slice(0, 5).map((item: IItemContent) => {
                  return <SuggestResultItem key={item.itemId} item={item} onClick={storePrevSearchItem} />;
                })}
                {totalResults > 1 && (
                  <div className={styles.suggestButton}>
                    <Button action={goToSearchSite} styles={[ButtonStyles.big]} type={ButtonType.default}>
                      {t("search.showAllResults--quantity", {quantity: totalResults.toLocaleString()})}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
        {/* endregion search suggestions */}

        <button
          id="CloseMobileSearchButton"
          className={styles.fieldButtonClose}
          type="button"
          aria-label={t("search.closeSearchField")}
          onClick={() => {
            closeSearchAndHeader();
            if (searchOpenBtn) searchOpenBtn.focus();
          }}
        >
          <i className="icon icon-left icon-size--22">&#160;</i>
        </button>
        <span onFocus={() => inputRef.current?.focus()} tabIndex={0}>
          {/* focusguard for better accessibility (focus on input field again, after reaching the last focusable element with keyboard navigation) */}
        </span>
      </div>
    </div>
  );
};

const HeaderSearch: React.FC = () => {
  const searchContainer = document.getElementById("HeaderSearchReact");

  if (!searchContainer) {
    return null;
  }

  return ReactDOM.createPortal(
    <ErrorBoundary fallback={null} occurrence="HeaderSearch">
      <Suspense fallback={null}>
        <HeaderSearchComponent />
      </Suspense>
    </ErrorBoundary>,
    searchContainer,
  );
};

export default HeaderSearch;
